// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import toUpper from 'lodash/toUpper';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import DamImage from '@tcp/core/src/components/common/atoms/DamImage';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import LinkText from '@tcp/core/src/components/common/molecules/LinkText';
import SafeComponent from '@tcp/core/src/components/common//hoc/withErrorBoundary';
import { getDynamicLabelValues } from '@tcp/core/src/utils/labels/labelUtilty';
import {
  configureInternalNavigationFromCMSUrl,
  parseBoolean,
  isClient,
  getDateObject,
} from '@tcp/core/src/utils';
import ModuleCountDownStyle, {
  StyledContainer,
  StyledTextContainer,
  StyledTimerContainer,
  StyledBackgroundWithColor,
  StyledNumberPart,
  StyledTextPart,
} from '../styles/ModuleCountDown.style';

const TIME_ZONE_VARIATION = 'fixed';
const TIME_ZONE = 'America/New_York';
const HIDE_TIMER = 'timer';
const HIDE_BANNER = 'banner';

const dayLabelVal = getDynamicLabelValues('dynamic.home.lbl_countdown_Days');

const hourLabelVal = getDynamicLabelValues('dynamic.home.lbl_countdown_Hours');

const minutesLabelVal = getDynamicLabelValues('dynamic.home.lbl_countdown_Minutes');

const secondLabelVal = getDynamicLabelValues('dynamic.home.lbl_countdown_Seconds');

export const convertTZ = (date, tzString) => {
  return getDateObject(
    (typeof date === 'string' ? getDateObject(date) : date).toLocaleString('en-US', {
      timeZone: tzString,
    })
  );
};

const checkValidStartDate = (sDate, timerVariation) => {
  const currentDate =
    timerVariation === TIME_ZONE_VARIATION
      ? convertTZ(getDateObject(), TIME_ZONE)
      : getDateObject();

  const startDate =
    timerVariation === TIME_ZONE_VARIATION
      ? convertTZ(getDateObject(sDate), TIME_ZONE)
      : getDateObject(sDate);

  return currentDate >= startDate;
};

export const calculateTimeLeft = (sDate, eDate, timerVariation) => {
  let startDate;
  let endDate;
  if (timerVariation === TIME_ZONE_VARIATION) {
    startDate = convertTZ(getDateObject(), TIME_ZONE);
    endDate = convertTZ(getDateObject(eDate), TIME_ZONE);
  } else {
    const arrEndDate = eDate && eDate.split('-');
    if (Array.isArray(arrEndDate)) {
      arrEndDate.pop();
      startDate = getDateObject();
      endDate = getDateObject(arrEndDate.join('-'));
    }
  }

  const difference = endDate - startDate;

  let timeLeft = {};

  if (difference > 0) {
    timeLeft = {
      Days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      Hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      Minutes: Math.floor((difference / 1000 / 60) % 60),
      Seconds: Math.floor((difference / 1000) % 60),
    };
  }

  return timeLeft;
};

const renderDamImage = ({ image, link }) => {
  const imgData = !isEmpty(image) ? image : {};
  let navigationUrl = {};

  if (link) {
    navigationUrl = link;
    navigationUrl.to = configureInternalNavigationFromCMSUrl(link?.url);
    navigationUrl.asPath = link?.url;
  }

  const damImageComp = (
    <DamImage
      imgData={imgData}
      className="module-countdown-img-full-width"
      link={link}
      isOptimizedVideo
      isHomePage
      lazyLoad
      isModule
    />
  );

  return (
    image && (
      <Anchor {...navigationUrl} className="image-link">
        {damImageComp}
      </Anchor>
    )
  );
};

const renderTextItem = (headerTextLine, navigation) => {
  return (
    <LinkText
      headerText={[headerTextLine]}
      locator="moduleContainer_header_text"
      textAlign="center"
      useStyle
      component="p"
      navigation={navigation}
      additionalClassName="countdown-header-text-line"
    />
  );
};

const getClassNameForTimer = (headerTextLine = []) => {
  const { textItems } =
    (headerTextLine && headerTextLine.length > 0 && !isEmpty(headerTextLine[0])) || {};
  const { style, styledObjWeb } =
    (textItems && textItems.length > 0 && !isEmpty(textItems[0])) || {};
  return { style, styledObjWeb };
};

const renderTimerComponents = (timerComponents, hideOption, timerFinished) => {
  if (hideOption === HIDE_TIMER && timerFinished) return null;
  return isClient() && timerComponents.length > 0 && timerComponents;
};

const getLinkedImage = (linkedImage) => linkedImage && linkedImage[0];

const showInterval = (intervalMap, interval, timeLeft) =>
  intervalMap[interval] && (timeLeft[interval] > 0 || interval === 'Seconds');

const getIntervalText = (interval) => {
  switch (interval) {
    case 'Days':
      return !dayLabelVal || dayLabelVal === 'lbl_countdown_Days' ? toUpper(interval) : dayLabelVal;
    case 'Hours':
      return !hourLabelVal || hourLabelVal === 'lbl_countdown_Hours'
        ? toUpper(interval)
        : hourLabelVal;
    case 'Minutes':
      return !minutesLabelVal || minutesLabelVal === 'lbl_countdown_Minutes'
        ? toUpper(interval)
        : minutesLabelVal;
    case 'Seconds':
      return !secondLabelVal || secondLabelVal === 'lbl_countdown_Seconds'
        ? toUpper(interval)
        : secondLabelVal;
    default:
      return toUpper(interval);
  }
};

const TextContainerCmp = (
  headerTextLine,
  navigation,
  timerComponents,
  hideOption,
  timerFinished
) => (
  <>
    {!isEmpty(headerTextLine) && renderTextItem(headerTextLine[0], navigation)}
    <StyledTimerContainer>
      {renderTimerComponents(timerComponents, hideOption, timerFinished)}
    </StyledTimerContainer>
  </>
);

const ModuleCountDown = ({
  countdown,
  className,
  navigation,
  assignKeyValue,
  styleOverrides = {},
}) => {
  const {
    headerTextLine,
    linkedImage,
    timerVariation,
    hideOption,
    startDateTime,
    endDateTime,
    days = '1',
    hours = '1',
    minutes = '1',
    seconds = '1',
  } = countdown;

  const isValidStartDate = checkValidStartDate(startDateTime, timerVariation);
  if (!isValidStartDate) return null;

  const [timeLeft, setTimeLeft] = useState(
    calculateTimeLeft(startDateTime, endDateTime, timerVariation)
  );
  const [timerFinished, setTimerFinished] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft(startDateTime, endDateTime, timerVariation));
    }, 1000);

    return () => clearTimeout(timer);
  });

  useEffect(() => {
    if (isEmpty(timeLeft)) {
      setTimerFinished(true);
    }
  }, [timeLeft]);

  const intervalMap = {
    Days: parseBoolean(days),
    Hours: parseBoolean(hours),
    Minutes: parseBoolean(minutes),
    Seconds: parseBoolean(seconds),
  };
  const timerComponents = [];
  const { style, styledObjWeb } = getClassNameForTimer(headerTextLine);
  const linkedImageCmp = getLinkedImage(linkedImage);
  const { image, color, link } = linkedImageCmp || {};
  let navigationUrl = {};
  if (link) {
    navigationUrl = link;
    navigationUrl.to = configureInternalNavigationFromCMSUrl(link?.url);
    navigationUrl.asPath = link?.url;
  }
  Object.keys(timeLeft).forEach((interval) => {
    if (timeLeft[interval] > 0 && timerFinished) {
      setTimerFinished(false);
    }
    if (showInterval(intervalMap, interval, timeLeft)) {
      timerComponents.push(
        <span className={style}>
          <StyledNumberPart
            className="numeric__part"
            styleOverrides={styleOverrides}
            customStyle={styledObjWeb}
          >
            {timeLeft[interval]}
          </StyledNumberPart>{' '}
          <StyledTextPart
            className="string__part"
            styleOverrides={styleOverrides}
            customStyle={styledObjWeb}
          >
            {getIntervalText(interval)}
          </StyledTextPart>
        </span>
      );
    }
  });

  return hideOption === HIDE_BANNER && timerFinished ? null : (
    <StyledContainer className={`module-countdown-container ${className}`} key={assignKeyValue}>
      {image && renderDamImage({ image, link })}
      {color ? (
        <StyledBackgroundWithColor color={color}>
          <Anchor {...navigationUrl} className="background-link">
            {TextContainerCmp(
              headerTextLine,
              navigation,
              timerComponents,
              hideOption,
              timerFinished
            )}
          </Anchor>
        </StyledBackgroundWithColor>
      ) : (
        <StyledTextContainer isImageView={!isEmpty(image)}>
          {TextContainerCmp(headerTextLine, navigation, timerComponents, hideOption, timerFinished)}
        </StyledTextContainer>
      )}
    </StyledContainer>
  );
};

ModuleCountDown.propTypes = {
  countdown: PropTypes.shape({
    headerTextLine: PropTypes.shape({}),
    linkedImage: PropTypes.arrayOf(
      PropTypes.shape({
        color: PropTypes.shape({}),
        image: PropTypes.shape({}),
        link: PropTypes.shape({}),
      })
    ),
    timerVariation: PropTypes.string.isRequired,
    hideOption: PropTypes.string,
    startDateTime: PropTypes.string,
    endDateTime: PropTypes.string,
    days: PropTypes.string,
    hours: PropTypes.string,
    minutes: PropTypes.string,
    seconds: PropTypes.string,
  }).isRequired,
  className: PropTypes.string,
  styleOverrides: PropTypes.shape({}).isRequired,
  navigation: PropTypes.shape({}).isRequired,
};

ModuleCountDown.defaultProps = {
  className: '',
};

ModuleCountDown.displayName = 'ModuleCountDown';

export default withStyles(SafeComponent(ModuleCountDown), ModuleCountDownStyle);
export { ModuleCountDown as ModuleCountDownVanilla };
